import { useCallback, useEffect, useState } from 'react'

/**
 * CONFIRMATION EN PLACE (#193, D2 / D5) — un geste qui engage (composer un
 * numéro, classer un prospect pour tout le plateau) ne part JAMAIS au premier
 * contact du doigt :
 *
 *   1ᵉʳ tap  → le bouton S'ARME : son libellé devient la question
 *              (« Appeler +33 6 … ? ») pendant `timeoutMs` ;
 *   2ᵉ tap   → l'action part ;
 *   sinon    → délai écoulé, ou tap n'importe où ailleurs : retour au repos.
 *
 * Trois gardes, chacune née d'un constat de recette sur iPad :
 *   ① un faux départ de défilement (6 px, 80 ms) produit un `click` — il ne
 *      fait qu'armer ;
 *   ② un double-tap (deux `click` à ~120 ms) ne confirme pas : le second est
 *      ignoré tant que l'armement a moins de `MIN_CONFIRM_DELAY_MS` ;
 *   ③ si la liste a BOUGÉ entre les deux taps (un bandeau s'insère au-dessus,
 *      une ligne disparaît), le bouton armé n'est plus sous le doigt : au lieu
 *      d'agir on ré-arme à la nouvelle position. La clé protège le reste — un
 *      tap qui tombe sur une AUTRE ligne ne fait qu'armer celle-là.
 *
 * Pas de `window.confirm` : une boîte modale du navigateur masque la ligne
 * qu'elle interroge et casse le geste sur iPad (PWA).
 */

export const ARMED_TIMEOUT_MS = 3000
/** En deçà, un second `click` est un double-tap, pas une confirmation. */
export const MIN_CONFIRM_DELAY_MS = 350
/** Déplacement vertical (px) au-delà duquel le bouton n'est plus « le même endroit ». */
const MOVE_TOLERANCE_PX = 4

interface Armed {
  key: string
  top: number
  at: number
}

export interface ArmedAction {
  /** Clé du bouton armé, ou `null` au repos. */
  armedKey: string | null
  /**
   * À appeler dans le `onClick` du bouton. Arme au premier appel, exécute
   * `action` au second (même clé, même endroit, délai respecté).
   */
  press: (key: string, element: HTMLElement, action: () => void) => void
  disarm: () => void
}

export function useArmedAction(timeoutMs: number = ARMED_TIMEOUT_MS): ArmedAction {
  const [armed, setArmed] = useState<Armed | null>(null)

  useEffect(() => {
    if (!armed) return
    const timer = window.setTimeout(() => setArmed(null), timeoutMs)
    // Tap ailleurs (y compris sur un autre bouton armable) → retour au repos.
    // `mousedown` + `touchstart` : jsdom n'a pas de PointerEvent (REGISTRE §5).
    const onPress = (event: Event) => {
      const target = event.target as Element | null
      const holder = target?.closest?.('[data-armed-key]')
      if (holder?.getAttribute('data-armed-key') === armed.key) return
      setArmed(null)
    }
    document.addEventListener('mousedown', onPress, true)
    document.addEventListener('touchstart', onPress, true)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', onPress, true)
      document.removeEventListener('touchstart', onPress, true)
    }
  }, [armed, timeoutMs])

  const press = useCallback(
    (key: string, element: HTMLElement, action: () => void) => {
      const top = element.getBoundingClientRect().top
      const now = Date.now()
      if (armed?.key !== key) {
        setArmed({ key, top, at: now })
        return
      }
      if (now - armed.at < MIN_CONFIRM_DELAY_MS) return // ② double-tap
      if (Math.abs(top - armed.top) > MOVE_TOLERANCE_PX) {
        setArmed({ key, top, at: now }) // ③ la liste a bougé
        return
      }
      setArmed(null)
      action()
    },
    [armed],
  )

  const disarm = useCallback(() => setArmed(null), [])

  return { armedKey: armed?.key ?? null, press, disarm }
}
